// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — no type declarations available
import search from 'approx-string-match'

import { normalizeText } from '@/lib/string/string-utils'

type ApproxMatch = { start: number; end: number; errors: number }

export type TextRange = { start: number; end: number }

export type MergedChunk = {
  mergedContext: string
  quotes: string[]
}

export const MIN_OVERLAP = 50

/** Maximum ratio of character errors allowed in fuzzy quote matching (5%) */
export const FUZZY_MATCH_ERROR_RATE = 0.05

export function findSuffixPrefixOverlap(a: string, b: string): number {
  const lowerA = a.toLowerCase()
  const lowerB = b.toLowerCase()
  const maxLen = Math.min(a.length, b.length)
  // Search from longest to shortest — return on first (longest) match
  for (let len = maxLen; len >= MIN_OVERLAP; len--) {
    if (lowerA.slice(lowerA.length - len) === lowerB.slice(0, len)) {
      return len
    }
  }
  return 0
}

export function mergeOverlappingContexts(quotes: string[], quoteContexts: string[]): MergedChunk[] {
  // Start with each (quote, context) as its own group
  const groups: MergedChunk[] = quotes.map((q, i) => ({
    mergedContext: quoteContexts[i] ?? '',
    quotes: [q],
  }))

  // Repeatedly try to merge pairs until no more merges possible
  let merged = true
  while (merged) {
    merged = false
    for (let i = 0; i < groups.length && !merged; i++) {
      for (let j = i + 1; j < groups.length && !merged; j++) {
        // Try both directions: i's suffix overlaps j's prefix, or j's suffix overlaps i's prefix
        const overlapAB = findSuffixPrefixOverlap(groups[i].mergedContext, groups[j].mergedContext)
        const overlapBA = findSuffixPrefixOverlap(groups[j].mergedContext, groups[i].mergedContext)

        if (overlapAB >= MIN_OVERLAP || overlapBA >= MIN_OVERLAP) {
          let combinedContext: string
          if (overlapAB >= overlapBA) {
            combinedContext = groups[i].mergedContext + groups[j].mergedContext.slice(overlapAB)
          } else {
            combinedContext = groups[j].mergedContext + groups[i].mergedContext.slice(overlapBA)
          }
          groups[i] = {
            mergedContext: combinedContext,
            quotes: [...groups[i].quotes, ...groups[j].quotes],
          }
          groups.splice(j, 1)
          merged = true
        }
      }
    }
  }

  return groups
}

function normalizeForMatch(text: string): { normalized: string; origIndices: number[] } {
  const afterNorm = normalizeText(text).toLowerCase()
  const origIndices: number[] = []
  let result = ''
  let lastWasSpace = false
  for (let i = 0; i < afterNorm.length; i++) {
    const ch = afterNorm[i]
    if (/\s/.test(ch)) {
      if (!lastWasSpace) {
        result += ' '
        origIndices.push(i)
        lastWasSpace = true
      }
    } else {
      result += ch
      origIndices.push(i)
      lastWasSpace = false
    }
  }
  return { normalized: result, origIndices }
}

/**
 * Best (lowest-error) approximate match of `needle` within `haystack`, or null
 * if none is found within the error budget. Inputs should already be normalized
 * and lowercased by the caller so the returned indices line up with `haystack`.
 */
export function approxSearchBest(
  haystack: string,
  needle: string,
  errorRate: number = FUZZY_MATCH_ERROR_RATE,
): TextRange | null {
  if (needle.length === 0 || haystack.length < needle.length) return null

  const maxErrors = Math.floor(needle.length * errorRate)
  const matches: ApproxMatch[] = search(haystack, needle, maxErrors)
  if (matches.length === 0) return null

  const best = matches.reduce((a: ApproxMatch, b: ApproxMatch) => (a.errors <= b.errors ? a : b))
  return { start: best.start, end: best.end }
}

export function fuzzyFindPosition(quote: string, context: string): TextRange | null {
  const { normalized: normQuote } = normalizeForMatch(quote)
  const { normalized: normContext, origIndices } = normalizeForMatch(context)

  if (normQuote.length === 0 || normContext.length < normQuote.length) return null

  const maxErrors = Math.floor(normQuote.length * FUZZY_MATCH_ERROR_RATE)
  const matches: ApproxMatch[] = search(normContext, normQuote, maxErrors)
  if (matches.length === 0) return null

  const best = matches.reduce((a: ApproxMatch, b: ApproxMatch) => (a.errors <= b.errors ? a : b))
  return {
    start: origIndices[best.start],
    end: origIndices[best.end - 1] + 1,
  }
}

export function sortAndMergeRanges(ranges: TextRange[]): TextRange[] {
  if (ranges.length === 0) return []
  const sorted = [...ranges].sort((a, b) => a.start - b.start)
  const merged: TextRange[] = [{ ...sorted[0] }]
  for (let i = 1; i < sorted.length; i++) {
    const current = merged[merged.length - 1]
    if (sorted[i].start <= current.end) {
      current.end = Math.max(current.end, sorted[i].end)
    } else {
      merged.push({ ...sorted[i] })
    }
  }
  return merged
}

export function findQuotePositions(quotes: string[], context: string): TextRange[] {
  const results: TextRange[] = []
  for (const quote of quotes) {
    const result = fuzzyFindPosition(quote, context)
    if (result) {
      results.push(result)
    }
  }
  return sortAndMergeRanges(results)
}
